import React, { useState, useEffect, useMemo, useContext, useRef } from "react";
import { Localization } from "@viamap/viamap2-common";
import { SelectLayer } from "../../components/CommonInputs";
import { MapitStateContext } from "src/states/MapitState";
import { MapitUtils } from "src/managers/MapitUtils";
import { JPBReferenceProperty, AreaUnit, SoilQualityGroup, SOIL_QUALITY_GROUPS, SOIL_INDEX, getSoilTypeColor } from "./JPBState";
import { formatPricePerSquareMeter, formatArea, formatPercentage, calculateSoilSpecificAgriculturalPrices, calculateAreaPrice, mapSoilTypeToGroup, calculateWeightedMeanForSeries } from "./JPBFunc";
import { area, combine, intersect, featureCollection } from "@turf/turf";
import { GlassInputGroup } from "../../components/GlassInput";
import { Spinner } from "react-bootstrap";
import { LayerVariabilityType } from "src/common/managers/Types";
import type { GeoJSONSource, Map as MaplibreMap } from "maplibre-gl";
import { DataCollector } from "src/dataCollector/DataCollector";
import { autoUpdate, FloatingPortal, offset, useDismiss, useFloating, useHover, useInteractions } from "@floating-ui/react";
import { ActionTracker } from "src/common/managers/ActionTracker";

const SOURCE_ID_POLYGON_ANALYZER_FARMLAND = 'jpb-polygon-analyzer-farmland';
const SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT = 'jpb-polygon-analyzer-jordbundskort';
const LAYER_ID_POLYGON_ANALYZER_FARMLAND = 'jpb-polygon-analyzer-farmland-layer';
const LAYER_ID_POLYGON_ANALYZER_JORDBUNDSKORT = 'jpb-polygon-analyzer-jordbundskort-layer';

function getFarmlandLayerYear(transactionDate: Date): number {
   const transactionYear = transactionDate.getFullYear();
   const earliestAvailableYear = 2008;
   const latestAvailableYear = 2025;
   const maxYearsBack = 5;

   if (transactionYear >= earliestAvailableYear && transactionYear <= latestAvailableYear) {
      return transactionYear;
   }

   if (transactionYear > latestAvailableYear) {
      return latestAvailableYear;
   }

   if (transactionYear < earliestAvailableYear) {
      const minAllowedYear = Math.max(earliestAvailableYear, transactionYear - maxYearsBack);
      return earliestAvailableYear;
   }

   return earliestAvailableYear;
}

async function fetchFarmlandOverlap(
   geoshape: any,
   transactionDate: Date,
   _abortSignal?: AbortSignal
): Promise<any | null> {
   if (!geoshape) {
      return null;
   }

   const layerYear = getFarmlandLayerYear(transactionDate);
   const layerName = `Marker_${layerYear}` as any;

   try {
      const featureCollection = {
         type: "FeatureCollection" as const,
         features: [{
            type: "Feature" as const,
            geometry: geoshape,
            properties: {}
         }]
      };

      const result = await DataCollector.WFS.getFeatureInsidePolygon(
         "FVM (Føde, Land og Fisk)" as any,
         layerName,
         featureCollection as any,
         {}
      );

      if (!result || !result.features || result.features.length === 0) {
         return null;
      }

      const geometries = result.features.map((feature: any) => feature.geometry);

      if (geometries.length === 0) {
         return null;
      }

      const farmlandFeatureCollection = {
         type: "FeatureCollection" as const,
         features: geometries.map((geom: any) => ({
            type: "Feature" as const,
            geometry: geom,
            properties: {}
         }))
      };

      const combined = combine(farmlandFeatureCollection as any);

      if (combined && combined.features && combined.features.length > 0) {
         const firstFeature = combined.features[0] as any;
         return firstFeature?.geometry || null;
      }

      return null;
   } catch (error) {
      console.error("Error fetching farmland overlap:", error);
      throw error;
   }
}

function calculateFarmlandIntersection(
   geoshape: any,
   farmlandGeometry: any
): { intersectionGeometry: any; overlapPercentage: number } | null {
   if (!geoshape || !farmlandGeometry) {
      return null;
   }

   try {
      const combinedFeatureCollection = featureCollection([
         {
            type: "Feature" as const,
            geometry: geoshape,
            properties: {}
         },
         {
            type: "Feature" as const,
            geometry: farmlandGeometry,
            properties: {}
         }
      ]);

      const intersection = intersect(combinedFeatureCollection);

      if (!intersection) {
         return {
            intersectionGeometry: null,
            overlapPercentage: 0
         };
      }

      const intersectionGeometry = intersection.geometry;

      const intersectionArea = area(intersection);
      const propertyArea = area({
         type: "Feature" as const,
         geometry: geoshape,
         properties: {}
      });

      if (propertyArea === 0) {
         return null;
      }

      const overlapPercentage = (intersectionArea / propertyArea) * 100;

      return {
         intersectionGeometry,
         overlapPercentage
      };
   } catch (error) {
      console.error("Error calculating farmland intersection:", error);
      return null;
   }
}

function calculateJordbundskortFarmlandIntersection(
   farmlandGeometry: any,
   jordbundskortGeometry: any
): { intersectionGeometry: any | null; overlapPercentages: { [soilType: string]: number } } | null {
   if (!farmlandGeometry || !jordbundskortGeometry) {
      return null;
   }

   try {
      if (jordbundskortGeometry.type !== "FeatureCollection" || !jordbundskortGeometry.features) {
         return null;
      }

      const intersectionFeatures: any[] = [];
      const overlapPercentages: { [soilType: string]: number } = {};

      const farmlandArea = area({
         type: "Feature" as const,
         geometry: farmlandGeometry,
         properties: {}
      });

      if (farmlandArea === 0) {
         return null;
      }

      jordbundskortGeometry.features.forEach((jordbundskortFeature: any) => {
         if (!jordbundskortFeature.geometry) {
            return;
         }

         try {
            const combinedFeatureCollection = featureCollection([
               {
                  type: "Feature" as const,
                  geometry: farmlandGeometry,
                  properties: {}
               },
               {
                  type: "Feature" as const,
                  geometry: jordbundskortFeature.geometry,
                  properties: {}
               }
            ]);

            const intersection = intersect(combinedFeatureCollection);

            if (intersection && intersection.geometry) {
               const intersectionArea = area(intersection);
               const overlapPercentage = (intersectionArea / farmlandArea) * 100;

               const jordtype = jordbundskortFeature.properties?.Jordtype ||
                  jordbundskortFeature.properties?.jordtype ||
                  'Unknown';

               if (overlapPercentages[jordtype]) {
                  overlapPercentages[jordtype] += overlapPercentage;
               } else {
                  overlapPercentages[jordtype] = overlapPercentage;
               }

               intersectionFeatures.push({
                  type: "Feature" as const,
                  geometry: intersection.geometry,
                  properties: {
                     ...(jordbundskortFeature.properties || {})
                  }
               });
            }
         } catch (error) {
            console.error("Error calculating intersection for jordbundskort feature:", error);
         }
      });

      if (intersectionFeatures.length === 0) {
         return null;
      }

      const totalPercentage = Object.values(overlapPercentages).reduce((sum, p) => sum + (typeof p === 'number' ? p : 0), 0);

      if (totalPercentage > 0 && Math.abs(totalPercentage - 100) > 0.01) {
         const normalizationFactor = 100 / totalPercentage;
         for (const jordtype in overlapPercentages) {
            if (typeof overlapPercentages[jordtype] === 'number') {
               overlapPercentages[jordtype] = overlapPercentages[jordtype] * normalizationFactor;
            }
         }
      }

      return {
         intersectionGeometry: {
            type: "FeatureCollection" as const,
            features: intersectionFeatures
         },
         overlapPercentages
      };
   } catch (error) {
      console.error("Error calculating jordbundskort-farmland intersection:", error);
      return null;
   }
}

async function fetchJordbundskortOverlap(
   geoshape: any,
   _abortSignal?: AbortSignal
): Promise<any | null> {
   if (!geoshape) {
      return null;
   }

   const layerName = "Jordbundskort_2024" as any;

   try {
      const featureCollection = {
         type: "FeatureCollection" as const,
         features: [{
            type: "Feature" as const,
            geometry: geoshape,
            properties: {}
         }]
      };

      const result = await DataCollector.WFS.getFeatureInsidePolygon(
         "FVM (Føde, Land og Fisk)" as any,
         layerName,
         featureCollection as any,
         {}
      );

      if (!result || !result.features || result.features.length === 0) {
         return null;
      }

      return {
         type: "FeatureCollection" as const,
         features: result.features.map((feature: any) => ({
            type: "Feature" as const,
            geometry: feature.geometry,
            properties: feature.properties || {}
         }))
      };
   } catch (error) {
      console.error("Error fetching jordbundskort overlap:", error);
      throw error;
   }
}

type JPBPolygonAnalyzerProps = {
   referenceProperties: JPBReferenceProperty[];
   checkedItems: Set<string>;
   areaUnit: AreaUnit;
}

type GeometryData = {
   polygonArea: number;
   farmlandOverlapArea: number;
   farmlandOverlapPercentage: number;
   soilComposition: { [soilType: string]: number };
   error?: string;
}

type AnalysisResult = {
   polygonArea: number;
   farmlandOverlapArea: number;
   farmlandOverlapPercentage: number;
   soilComposition: { [soilGroup: string]: number };
   estimatedPricePerSquareMeter: number | null;
   error?: string;
}

function SoilDistributionBar(props: {
   analysisResult: AnalysisResult;
   hoveredSoilType: string | null;
   hoveredSoilPercentage: number | null;
   hoveredNonFarmland: boolean;
   setHoveredSoilType: (type: string | null) => void;
   setHoveredSoilPercentage: (percentage: number | null) => void;
   setHoveredNonFarmland: (hovered: boolean) => void;
}) {
   const { analysisResult, hoveredSoilType, hoveredSoilPercentage, hoveredNonFarmland, setHoveredSoilType, setHoveredSoilPercentage, setHoveredNonFarmland } = props;
   const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
   const [nonFarmlandReferenceElement, setNonFarmlandReferenceElement] = useState<HTMLElement | null>(null);

   const { refs: tooltipRefs, floatingStyles: tooltipFloatingStyles, context: tooltipContext } = useFloating({
      whileElementsMounted: autoUpdate,
      open: hoveredSoilType !== null,
      middleware: [offset(8)],
      placement: 'top',
      elements: {
         reference: referenceElement
      }
   });

   const { refs: nonFarmlandTooltipRefs, floatingStyles: nonFarmlandTooltipFloatingStyles, context: nonFarmlandTooltipContext } = useFloating({
      whileElementsMounted: autoUpdate,
      open: hoveredNonFarmland,
      middleware: [offset(8)],
      placement: 'top',
      elements: {
         reference: nonFarmlandReferenceElement
      }
   });

   const { getFloatingProps: getTooltipFloatingProps } = useInteractions([
      useDismiss(tooltipContext)
   ]);

   const { getFloatingProps: getNonFarmlandTooltipFloatingProps } = useInteractions([
      useDismiss(nonFarmlandTooltipContext)
   ]);

   return (
      <div style={{ marginTop: "20px" }}>
         <div style={{ marginBottom: "10px", fontWeight: "600" }}>
            {Localization.getText("Area Distribution")}
         </div>
         <div style={{
            width: "100%",
            height: "30px",
            backgroundColor: "#e0e0e0",
            borderRadius: "4px",
            overflow: "hidden",
            display: "flex",
            position: "relative"
         }}>
            <div style={{
               width: `${analysisResult.farmlandOverlapPercentage}%`,
               backgroundColor: "#4CAF50",
               position: "relative",
               display: "flex"
            }}>
               {Object.entries(analysisResult.soilComposition).map(([soilType, percentage], index, arr) => {
                  const cumulativePercentage = arr.slice(0, index).reduce((sum, [, p]) => sum + p, 0);
                  const width = percentage;
                  const percentageOfTotal = (analysisResult.farmlandOverlapPercentage * percentage) / 100;
                  const soilColor = getSoilTypeColor(soilType);
                  const isHovered = hoveredSoilType === soilType;

                  return (
                     <div
                        key={soilType}
                        ref={(el) => {
                           if (isHovered && el) {
                              setReferenceElement(el);
                              tooltipRefs.setReference(el);
                           }
                        }}
                        style={{
                           position: "absolute",
                           left: `${cumulativePercentage}%`,
                           width: `${width}%`,
                           height: "100%",
                           backgroundColor: soilColor,
                           borderRight: index < arr.length - 1 ? "1px solid rgba(0,0,0,0.1)" : "none",
                           cursor: "pointer",
                           opacity: isHovered ? 0.9 : 1,
                           transition: "opacity 0.2s"
                        }}
                        onMouseEnter={(e) => {
                           setReferenceElement(e.currentTarget);
                           tooltipRefs.setReference(e.currentTarget);
                           setHoveredSoilType(soilType);
                           setHoveredSoilPercentage(percentageOfTotal);
                        }}
                        onMouseLeave={() => {
                           setHoveredSoilType(null);
                           setHoveredSoilPercentage(null);
                           setReferenceElement(null);
                        }}
                     />
                  );
               })}
            </div>
            <div
               ref={(el) => {
                  if (hoveredNonFarmland && el) {
                     setNonFarmlandReferenceElement(el);
                     nonFarmlandTooltipRefs.setReference(el);
                  }
               }}
               style={{
                  width: `${100 - analysisResult.farmlandOverlapPercentage}%`,
                  backgroundColor: "#9E9E9E",
                  cursor: "pointer",
                  opacity: hoveredNonFarmland ? 0.9 : 1,
                  transition: "opacity 0.2s"
               }}
               onMouseEnter={(e) => {
                  setNonFarmlandReferenceElement(e.currentTarget);
                  nonFarmlandTooltipRefs.setReference(e.currentTarget);
                  setHoveredNonFarmland(true);
               }}
               onMouseLeave={() => {
                  setHoveredNonFarmland(false);
                  setNonFarmlandReferenceElement(null);
               }}
            />
         </div>
         {hoveredSoilType !== null && hoveredSoilPercentage !== null && (
            <FloatingPortal>
               <div
                  ref={tooltipRefs.setFloating}
                  style={{
                     ...tooltipFloatingStyles,
                     zIndex: 1000,
                     backgroundColor: "rgba(0, 0, 0, 0.85)",
                     color: "white",
                     padding: "8px 12px",
                     borderRadius: "6px",
                     fontSize: "13px",
                     fontWeight: "500",
                     pointerEvents: "none",
                     boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
                  }}
                  {...getTooltipFloatingProps()}
               >
                  <div style={{ fontWeight: "600", marginBottom: "2px" }}>{hoveredSoilType}</div>
                  <div style={{ fontSize: "12px", opacity: 0.9 }}>
                     {Localization.getText("Percentage")}: {formatPercentage(hoveredSoilPercentage)}
                  </div>
               </div>
            </FloatingPortal>
         )}
         {hoveredNonFarmland && (
            <FloatingPortal>
               <div
                  ref={nonFarmlandTooltipRefs.setFloating}
                  style={{
                     ...nonFarmlandTooltipFloatingStyles,
                     zIndex: 1000,
                     backgroundColor: "rgba(0, 0, 0, 0.85)",
                     color: "white",
                     padding: "8px 12px",
                     borderRadius: "6px",
                     fontSize: "13px",
                     fontWeight: "500",
                     pointerEvents: "none",
                     boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
                  }}
                  {...getNonFarmlandTooltipFloatingProps()}
               >
                  <div style={{ fontWeight: "600", marginBottom: "2px" }}>
                     {Localization.getText("Non-Farmland")}
                  </div>
                  <div style={{ fontSize: "12px", opacity: 0.9 }}>
                     {Localization.getText("Percentage")}: {formatPercentage(100 - analysisResult.farmlandOverlapPercentage)}
                  </div>
               </div>
            </FloatingPortal>
         )}
         <div style={{ display: "flex", justifyContent: "space-between", marginTop: "5px", fontSize: "12px", color: "#666" }}>
            <span>
               {Localization.getText("Farmland")}: {formatPercentage(analysisResult.farmlandOverlapPercentage)}
            </span>
            <span>
               {Localization.getText("Non-Farmland")}: {formatPercentage(100 - analysisResult.farmlandOverlapPercentage)}
            </span>
         </div>
      </div>
   );
}

export function JPBPolygonAnalyzer(props: JPBPolygonAnalyzerProps) {
   const { state: mapitState } = useContext(MapitStateContext);
   const [selectedLayerId, setSelectedLayerId] = useState<number | undefined>(undefined);
   const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
   const [isCalculating, setIsCalculating] = useState<boolean>(false);
   const [isFetchingGeometry, setIsFetchingGeometry] = useState<boolean>(false);
   const [geometryData, setGeometryData] = useState<GeometryData | null>(null);
   const [farmlandIntersectionGeometry, setFarmlandIntersectionGeometry] = useState<any | null>(null);
   const [jordbundskortIntersectionGeometry, setJordbundskortIntersectionGeometry] = useState<any | null>(null);
   const [hoveredSoilType, setHoveredSoilType] = useState<string | null>(null);
   const [hoveredSoilPercentage, setHoveredSoilPercentage] = useState<number | null>(null);
   const [hoveredNonFarmland, setHoveredNonFarmland] = useState<boolean>(false);

   // Cache for layers that failed due to timeout - prevents retrying
   const failedLayersRef = useRef<Set<number>>(new Set());
   const timeoutRef = useRef<NodeJS.Timeout | null>(null);

   const polygonLayers = useMemo(() => {
      return Object.values(mapitState.layers)
         .filter(layer => layer.layerVariability !== LayerVariabilityType.FixedFeatureLayer)
         .filter(layer => {
            return MapitUtils.isAreaLayer(layer.type) ||
               layer.type === "GeoJSON_Polygon" ||
               (layer.geoJson && layer.geoJson.features && layer.geoJson.features.length > 0 &&
                  layer.geoJson.features[0]?.geometry?.type === "Polygon");
         });
   }, [mapitState.layers]);

   const selectedLayer = useMemo(() => {
      if (selectedLayerId === undefined) return null;
      return mapitState.layers[selectedLayerId] || null;
   }, [selectedLayerId, mapitState.layers]);

   const selectedPolygon = useMemo(() => {
      if (!selectedLayer || !selectedLayer.geoJson || !selectedLayer.geoJson.features || selectedLayer.geoJson.features.length === 0) {
         return null;
      }

      const polygonFeatures = selectedLayer.geoJson.features.filter((f: any) =>
         f.geometry && (f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon")
      );

      if (polygonFeatures.length === 0) {
         return null;
      }

      if (polygonFeatures.length === 1) {
         return polygonFeatures[0];
      }

      try {
         const featureCollectionToCombine = featureCollection(polygonFeatures);
         const combined = combine(featureCollectionToCombine as any);

         if (combined && combined.features && combined.features.length > 0) {
            const combinedGeometry = combined.features[0]?.geometry;
            if (combinedGeometry) {
               return {
                  type: "Feature" as const,
                  geometry: combinedGeometry,
                  properties: {}
               };
            }
         }
      } catch (error) {
         console.error("Error combining polygon features:", error);
         return polygonFeatures[0];
      }

      return polygonFeatures[0];
   }, [selectedLayer]);

   useEffect(() => {
      if (!selectedPolygon || props.referenceProperties.length === 0 || props.checkedItems.size === 0) {
         setGeometryData(null);
         setFarmlandIntersectionGeometry(null);
         setJordbundskortIntersectionGeometry(null);
         setAnalysisResult(null);
         // Clear timeout if component unmounts or dependencies change
         if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
         }
         return;
      }

      // Check if this layer has already failed due to timeout
      if (selectedLayerId !== undefined && failedLayersRef.current.has(selectedLayerId)) {
         setGeometryData({
            polygonArea: 0,
            farmlandOverlapArea: 0,
            farmlandOverlapPercentage: 0,
            soilComposition: {},
            error: "Cannot calculate - calculation timeout"
         });
         setIsFetchingGeometry(false);
         return;
      }

      setIsFetchingGeometry(true);

      const polygonGeometry = selectedPolygon.geometry;
      const polygonAreaM2 = area({
         type: "Feature" as const,
         geometry: polygonGeometry,
         properties: {}
      });


      const MAX_AREA_M2 = 10_000_000;
      if (polygonAreaM2 > MAX_AREA_M2) {
         const areaHa = polygonAreaM2 / 10_000;
         setGeometryData({
            polygonArea: polygonAreaM2,
            farmlandOverlapArea: 0,
            farmlandOverlapPercentage: 0,
            soilComposition: {},
            error: Localization.getFormattedText("Polygon area too large ({area} ha). Maximum allowed: 1000 ha. Please select a smaller polygon.", { area: areaHa.toFixed(1) })
         });
         setIsFetchingGeometry(false);
         return;
      }

      const checkedProperties = props.referenceProperties.filter(p => props.checkedItems.has(p.documentID));
      const averageDate = checkedProperties.length > 0
         ? new Date(checkedProperties.reduce((sum, p) => sum + p.transactionDate.getTime(), 0) / checkedProperties.length)
         : new Date();

      // Create a timeout promise that rejects after 10 seconds
      const timeoutPromise = new Promise<never>((_, reject) => {
         timeoutRef.current = setTimeout(() => {
            reject(new Error("Calculation timeout - area too large to calculate"));
         }, 10000); // 10 seconds
      });

      (async () => {
         try {

            // Wrap the calculation in Promise.race with timeout
            const calculationPromise = (async () => {
               let farmlandGeometry: any | null = null;
               let farmlandIntersectionResult: { intersectionGeometry: any; overlapPercentage: number } | null = null;

               try {
                  farmlandGeometry = await fetchFarmlandOverlap(
                     polygonGeometry,
                     averageDate
                  );

                  if (farmlandGeometry) {
                     farmlandIntersectionResult = calculateFarmlandIntersection(
                        polygonGeometry,
                        farmlandGeometry
                     );
                  }
               } catch (error) {
                  console.error("Error fetching farmland overlap:", error);
               }

               const farmlandOverlapAreaM2 = farmlandIntersectionResult?.intersectionGeometry
                  ? area({
                     type: "Feature" as const,
                     geometry: farmlandIntersectionResult.intersectionGeometry,
                     properties: {}
                  })
                  : 0;

               const farmlandOverlapPercentage = farmlandIntersectionResult?.overlapPercentage ?? 0;

               let soilComposition: { [soilType: string]: number } = {};
               let jordbundskortIntersection: any | null = null;

               try {
                  const jordbundskortGeometry = await fetchJordbundskortOverlap(
                     polygonGeometry
                  );

                  const farmlandIntersectionGeom = farmlandIntersectionResult?.intersectionGeometry;
                  if (farmlandIntersectionGeom && jordbundskortGeometry) {
                     const intersectionResult = calculateJordbundskortFarmlandIntersection(
                        farmlandIntersectionGeom,
                        jordbundskortGeometry
                     );

                     if (intersectionResult) {
                        soilComposition = intersectionResult.overlapPercentages;
                        jordbundskortIntersection = intersectionResult.intersectionGeometry;
                     }
                  }
               } catch (error) {
                  console.error("Error fetching jordbundskort overlap:", error);
               }

               return {
                  farmlandIntersectionGeometry: farmlandIntersectionResult?.intersectionGeometry || null,
                  jordbundskortIntersection: jordbundskortIntersection || null,
                  geometryData: {
                     polygonArea: polygonAreaM2,
                     farmlandOverlapArea: farmlandOverlapAreaM2,
                     farmlandOverlapPercentage: farmlandOverlapPercentage,
                     soilComposition: soilComposition
                  }
               };
            })();

            // Race between calculation and timeout
            const result = await Promise.race([calculationPromise, timeoutPromise]);

            // Clear timeout if calculation completed successfully
            if (timeoutRef.current) {
               clearTimeout(timeoutRef.current);
               timeoutRef.current = null;
            }

            setFarmlandIntersectionGeometry(result.farmlandIntersectionGeometry);
            setJordbundskortIntersectionGeometry(result.jordbundskortIntersection);
            setGeometryData(result.geometryData);
         } catch (error: any) {
            // Clear timeout ref
            if (timeoutRef.current) {
               clearTimeout(timeoutRef.current);
               timeoutRef.current = null;
            }

            // If it's a timeout error, mark this layer as failed
            if (error.message && error.message.includes("timeout")) {
               if (selectedLayerId !== undefined) {
                  failedLayersRef.current.add(selectedLayerId);
               }
               console.warn("Calculation timeout for layer:", selectedLayerId);
               setGeometryData({
                  polygonArea: 0,
                  farmlandOverlapArea: 0,
                  farmlandOverlapPercentage: 0,
                  soilComposition: {},
                  error: "Cannot calculate - calculation timeout"
               });
            } else {
               console.error("Error fetching geometry data:", error);
               setGeometryData({
                  polygonArea: 0,
                  farmlandOverlapArea: 0,
                  farmlandOverlapPercentage: 0,
                  soilComposition: {},
                  error: error.message || "Unknown error"
               });
            }
         } finally {
            setIsFetchingGeometry(false);
         }
      })();
   }, [selectedPolygon, selectedLayerId]);

   useEffect(() => {
      if (selectedLayerId === undefined) return
      ActionTracker.track("JPBModule", "Action", "SelectLayerForSimulation");
   }, [selectedLayerId]);

   // Cleanup timeout and reset failed layers cache when layer changes
   useEffect(() => {
      // Clear failed layers cache when switching layers - allows retry with different polygon
      failedLayersRef.current.clear();

      return () => {
         if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
         }
      };
   }, [selectedLayerId]);

   useEffect(() => {
      if (!geometryData || props.referenceProperties.length === 0 || props.checkedItems.size === 0) {
         setAnalysisResult(null);
         return;
      }

      setIsCalculating(true);

      const checkedProperties = props.referenceProperties.filter(p => props.checkedItems.has(p.documentID));

      try {
         const soilPrices = calculateSoilSpecificAgriculturalPrices(checkedProperties);

         let estimatedPrice: number | null = null;
         if (soilPrices && geometryData.farmlandOverlapPercentage > 0 && Object.keys(geometryData.soilComposition).length > 0) {
            try {
               const agriFraction = geometryData.farmlandOverlapPercentage / 100;

               const soilCompositionByGroup: { [soilGroup: string]: number } = {};

               for (const [soilType, percentage] of Object.entries(geometryData.soilComposition)) {
                  const soilGroup = mapSoilTypeToGroup(soilType);
                  if (soilGroup && SOIL_QUALITY_GROUPS.includes(soilGroup)) {
                     const fraction = percentage / 100;
                     if (soilCompositionByGroup[soilGroup]) {
                        soilCompositionByGroup[soilGroup] += fraction;
                     } else {
                        soilCompositionByGroup[soilGroup] = fraction;
                     }
                  }
               }

               const totalFraction = Object.values(soilCompositionByGroup).reduce((a, b) => a + b, 0);
               if (totalFraction > 0 && Math.abs(totalFraction - 1) > 0.01) {
                  const normalizationFactor = 1 / totalFraction;
                  for (const group in soilCompositionByGroup) {
                     soilCompositionByGroup[group] *= normalizationFactor;
                  }
               }

               const hasAllPrices = Object.keys(soilCompositionByGroup).every(
                  group => soilPrices[group as SoilQualityGroup] !== undefined
               );

               if (hasAllPrices && Object.keys(soilCompositionByGroup).length > 0) {
                  estimatedPrice = calculateAreaPrice(
                     agriFraction,
                     soilCompositionByGroup,
                     soilPrices as { [soilGroup: string]: number },
                     0.5
                  );
               }
            } catch (e) {
               console.warn("Failed to calculate estimated price:", e);
            }
         }

         setAnalysisResult({
            polygonArea: geometryData.polygonArea,
            farmlandOverlapArea: geometryData.farmlandOverlapArea,
            farmlandOverlapPercentage: geometryData.farmlandOverlapPercentage,
            soilComposition: geometryData.soilComposition,
            estimatedPricePerSquareMeter: estimatedPrice,
            error: geometryData.error
         });
      } catch (error: any) {
         console.error("Error calculating price:", error);
         setAnalysisResult({
            polygonArea: geometryData.polygonArea,
            farmlandOverlapArea: geometryData.farmlandOverlapArea,
            farmlandOverlapPercentage: geometryData.farmlandOverlapPercentage,
            soilComposition: geometryData.soilComposition,
            estimatedPricePerSquareMeter: null,
            error: error.message || "Unknown error"
         });
      } finally {
         setIsCalculating(false);
      }
   }, [geometryData, props.referenceProperties, props.checkedItems]);

   useEffect(() => {
      if (!mapitState.map) return;
      const map = mapitState.map.getMapPop();
      if (!map) return;


      const farmlandGeoJson = farmlandIntersectionGeometry
         ? {
            type: "FeatureCollection" as const,
            features: [{
               type: "Feature" as const,
               geometry: farmlandIntersectionGeometry,
               properties: {}
            }]
         }
         : {
            type: "FeatureCollection" as const,
            features: []
         };

      if (map.getSource(SOURCE_ID_POLYGON_ANALYZER_FARMLAND)) {
         const source = map.getSource(SOURCE_ID_POLYGON_ANALYZER_FARMLAND) as GeoJSONSource;
         if (source) source.setData(farmlandGeoJson as any);
      } else {
         map.addSource(SOURCE_ID_POLYGON_ANALYZER_FARMLAND, {
            type: 'geojson',
            data: farmlandGeoJson as any
         });
      }

      if (!map.getLayer(LAYER_ID_POLYGON_ANALYZER_FARMLAND)) {
         const farmlandLayerSpec: any = {
            id: LAYER_ID_POLYGON_ANALYZER_FARMLAND,
            type: 'fill',
            source: SOURCE_ID_POLYGON_ANALYZER_FARMLAND,
            paint: {
               'fill-pattern': "mit-patt-char-🚜-1",
               'fill-opacity': ["interpolate", ["linear"], ["zoom"], 13, 0, 14, 0.6]
            }
         };
         map.addLayer(farmlandLayerSpec);
      }

      if (!map.getLayer(LAYER_ID_POLYGON_ANALYZER_FARMLAND + "-outline")) {
         const farmlandOutlineLayerSpec: any = {
            id: LAYER_ID_POLYGON_ANALYZER_FARMLAND + "-outline",
            type: 'line',
            source: SOURCE_ID_POLYGON_ANALYZER_FARMLAND,
            paint: {
               'line-color': '#ff6b00',
               'line-opacity': ["interpolate", ["linear"], ["zoom"], 12.5, 0, 13.5, 1],
               'line-width': 2
            }
         };
         map.addLayer(farmlandOutlineLayerSpec);
      }

      const jordbundskortGeoJson = jordbundskortIntersectionGeometry
         ? (jordbundskortIntersectionGeometry.type === "FeatureCollection"
            ? jordbundskortIntersectionGeometry
            : {
               type: "FeatureCollection" as const,
               features: [{
                  type: "Feature" as const,
                  geometry: jordbundskortIntersectionGeometry,
                  properties: {}
               }]
            })
         : {
            type: "FeatureCollection" as const,
            features: []
         };

      if (jordbundskortGeoJson.type === "FeatureCollection" && jordbundskortGeoJson.features) {
         jordbundskortGeoJson.features = jordbundskortGeoJson.features.map((feature: any) => ({
            ...feature,
            properties: {
               ...feature.properties,
               color: getSoilTypeColor(feature.properties?.Jordtype || feature.properties?.jordtype)
            }
         }));
      }

      if (map.getSource(SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT)) {
         const source = map.getSource(SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT) as GeoJSONSource;
         if (source) source.setData(jordbundskortGeoJson as any);
      } else {
         map.addSource(SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT, {
            type: 'geojson',
            data: jordbundskortGeoJson as any
         });
      }

      if (!map.getLayer(LAYER_ID_POLYGON_ANALYZER_JORDBUNDSKORT)) {
         const jordbundskortLayerSpec: any = {
            id: LAYER_ID_POLYGON_ANALYZER_JORDBUNDSKORT,
            type: 'fill',
            source: SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT,
            paint: {
               'fill-color': ['get', 'color'],
               'fill-opacity': ["interpolate", ["linear"], ["zoom"], 13, 0, 14, 0.7],
               'fill-antialias': false,
               'fill-outline-color': '#00000000'
            }
         };
         map.addLayer(jordbundskortLayerSpec);
      }

      return () => {
         if (map.getLayer(LAYER_ID_POLYGON_ANALYZER_JORDBUNDSKORT)) {
            map.removeLayer(LAYER_ID_POLYGON_ANALYZER_JORDBUNDSKORT);
         }
         if (map.getLayer(LAYER_ID_POLYGON_ANALYZER_FARMLAND + "-outline")) {
            map.removeLayer(LAYER_ID_POLYGON_ANALYZER_FARMLAND + "-outline");
         }
         if (map.getLayer(LAYER_ID_POLYGON_ANALYZER_FARMLAND)) {
            map.removeLayer(LAYER_ID_POLYGON_ANALYZER_FARMLAND);
         }
         if (map.getSource(SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT)) {
            map.removeSource(SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT);
         }
         if (map.getSource(SOURCE_ID_POLYGON_ANALYZER_FARMLAND)) {
            map.removeSource(SOURCE_ID_POLYGON_ANALYZER_FARMLAND);
         }
      };
   }, [mapitState.map, farmlandIntersectionGeometry, jordbundskortIntersectionGeometry]);

   if (polygonLayers.length === 0) {
      return null;
   }

   return (
      <div className="jpb-polygon-analyzer-root">
         <div className="jpb-card jpb-card--padded" style={{ overflow: "hidden" }}>
            <h3 style={{ marginTop: 0, marginBottom: "15px", fontSize: "18px", fontWeight: "600" }}>
               {Localization.getText("Polygon Price Analyzer")}
            </h3>

            <GlassInputGroup>
               <div className="GlassInputLabel" style={{ marginBottom: "10px" }}>
                  {Localization.getText("Select Polygon Layer")}
               </div>
               <div style={{ marginBottom: "15px" }}>
                  <SelectLayer
                     selectedLayerId={selectedLayerId || ""}
                     onSelect={(layerId) => {
                        setSelectedLayerId(layerId);
                     }}
                     availableLayers={polygonLayers}
                  />
               </div>
            </GlassInputGroup>

            {(isFetchingGeometry || isCalculating) && (
               <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "15px" }}>
                  <Spinner size="sm" />
                  <span>
                     {isFetchingGeometry
                        ? Localization.getText("Fetching geometry data...")
                        : Localization.getText("Calculating price...")}
                  </span>
               </div>
            )}

            {analysisResult && !isFetchingGeometry && !isCalculating && (
               <div style={{ marginTop: "20px" }}>
                  {analysisResult.error ? (
                     <div style={{ color: "#d32f2f", marginBottom: "10px", padding: "10px", backgroundColor: "#ffebee", borderRadius: "4px", border: "1px solid #ffcdd2" }}>
                        <strong>{Localization.getText("Error")}:</strong> {analysisResult.error}
                     </div>
                  ) : (
                     <>
                        {analysisResult.farmlandOverlapPercentage > 0 && (
                           <SoilDistributionBar
                              analysisResult={analysisResult}
                              hoveredSoilType={hoveredSoilType}
                              hoveredSoilPercentage={hoveredSoilPercentage}
                              hoveredNonFarmland={hoveredNonFarmland}
                              setHoveredSoilType={setHoveredSoilType}
                              setHoveredSoilPercentage={setHoveredSoilPercentage}
                              setHoveredNonFarmland={setHoveredNonFarmland}
                           />
                        )}
                        <div style={{
                           marginTop: "20px",
                           border: "1px solid #ddd",
                           borderRadius: "4px",
                           overflow: "hidden"
                        }}>
                           <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                              <thead>
                                 <tr style={{ backgroundColor: "#f5f5f5", borderBottom: "2px solid #ddd" }}>
                                    <th style={{ padding: "10px", textAlign: "left", fontWeight: "600" }}>
                                       {Localization.getText("Category")}
                                    </th>
                                    <th style={{ padding: "10px", textAlign: "right", fontWeight: "600" }}>
                                       {Localization.getText("M_Area")}
                                    </th>
                                    <th style={{ padding: "10px", textAlign: "right", fontWeight: "600" }}>
                                       {Localization.getText("Percentage")}
                                    </th>
                                    <th style={{ padding: "10px", textAlign: "right", fontWeight: "600" }}>
                                       {Localization.getText("Price per")} {props.areaUnit === AreaUnit.Hectare ? "ha" : "m²"}
                                    </th>
                                 </tr>
                              </thead>
                              <tbody>
                                 <tr style={{ borderBottom: "1px solid #eee", backgroundColor: "#fff" }}>
                                    <td style={{ padding: "10px", fontWeight: "600" }}>
                                       {Localization.getText("Total Polygon Area")}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {formatArea(analysisResult.polygonArea, props.areaUnit)}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {formatPercentage(100)}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       -
                                    </td>
                                 </tr>

                                 {(() => {
                                    const checkedProperties = props.referenceProperties.filter(p => props.checkedItems.has(p.documentID));
                                    const avgEffectiveMarkPrice = calculateWeightedMeanForSeries(checkedProperties, "effectiveMark") ?? 0;

                                    return (
                                       <tr style={{ borderBottom: "1px solid #eee", backgroundColor: "#fff" }}>
                                          <td style={{ padding: "10px", fontWeight: "600", paddingLeft: "20px" }}>
                                             {Localization.getText("Farmland (Mark)")}
                                          </td>
                                          <td style={{ padding: "10px", textAlign: "right" }}>
                                             {formatArea(analysisResult.farmlandOverlapArea, props.areaUnit)}
                                          </td>
                                          <td style={{ padding: "10px", textAlign: "right" }}>
                                             {formatPercentage(analysisResult.farmlandOverlapPercentage)}
                                          </td>
                                          <td style={{ padding: "10px", textAlign: "right" }}>
                                             {avgEffectiveMarkPrice > 0
                                                ? formatPricePerSquareMeter(avgEffectiveMarkPrice, props.areaUnit)
                                                : "-"}
                                          </td>
                                       </tr>
                                    );
                                 })()}

                                 {(() => {
                                    const checkedProperties = props.referenceProperties.filter(p => props.checkedItems.has(p.documentID));
                                    const soilPrices = calculateSoilSpecificAgriculturalPrices(checkedProperties);
                                    const farmlandAreaM2 = analysisResult.farmlandOverlapArea;

                                    return Object.entries(analysisResult.soilComposition)
                                       .sort(([a], [b]) => {
                                          const groupA = mapSoilTypeToGroup(a);
                                          const groupB = mapSoilTypeToGroup(b);
                                          if (!groupA || !groupB) return 0;
                                          const indexA = SOIL_INDEX[groupA] || 0;
                                          const indexB = SOIL_INDEX[groupB] || 0;
                                          return indexB - indexA;
                                       })
                                       .map(([soilType, percentage]) => {
                                          const soilGroup = mapSoilTypeToGroup(soilType);
                                          const soilAreaM2 = (farmlandAreaM2 * percentage) / 100;
                                          const percentageOfTotal = (analysisResult.farmlandOverlapPercentage * percentage) / 100;
                                          const soilPrice = soilGroup && soilPrices ? soilPrices[soilGroup] : undefined;

                                          return (
                                             <tr key={soilType} style={{ borderBottom: "1px solid #eee", backgroundColor: "#fff" }}>
                                                <td style={{ padding: "10px", paddingLeft: "40px", fontStyle: "italic" }}>
                                                   {soilType} {soilGroup && `(${soilGroup})`}
                                                </td>
                                                <td style={{ padding: "10px", textAlign: "right" }}>
                                                   {formatArea(soilAreaM2, props.areaUnit)}
                                                </td>
                                                <td style={{ padding: "10px", textAlign: "right" }}>
                                                   {formatPercentage(percentageOfTotal)}
                                                </td>
                                                <td style={{ padding: "10px", textAlign: "right" }}>
                                                   {soilPrice !== undefined
                                                      ? formatPricePerSquareMeter(soilPrice, props.areaUnit)
                                                      : "-"}
                                                </td>
                                             </tr>
                                          );
                                       });
                                 })()}

                                 <tr style={{ borderBottom: "1px solid #eee", backgroundColor: "#fff" }}>
                                    <td style={{ padding: "10px", fontWeight: "600", paddingLeft: "20px" }}>
                                       {Localization.getText("Non-Farmland (Non-Mark)")}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {formatArea(analysisResult.polygonArea - analysisResult.farmlandOverlapArea, props.areaUnit)}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {formatPercentage(100 - analysisResult.farmlandOverlapPercentage)}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {(() => {
                                          const checkedProperties = props.referenceProperties.filter(p => props.checkedItems.has(p.documentID));
                                          const avgEffectiveMarkPrice = calculateWeightedMeanForSeries(checkedProperties, "effectiveMark") ?? 0;

                                          if (avgEffectiveMarkPrice <= 0) return "-";

                                          const nonAgriPrice = avgEffectiveMarkPrice * 0.5;
                                          return formatPricePerSquareMeter(nonAgriPrice, props.areaUnit);
                                       })()}
                                    </td>
                                 </tr>

                                 {analysisResult.estimatedPricePerSquareMeter !== null && (
                                    <tr style={{ borderTop: "2px solid #3a8e87", backgroundColor: "#fff" }}>
                                       <td style={{ padding: "15px", fontWeight: "600", fontSize: "16px" }}>
                                          {Localization.getText("Estimated Price per")} {props.areaUnit === AreaUnit.Hectare ? "ha" : "m²"}
                                       </td>
                                       <td colSpan={2} style={{ padding: "15px" }}></td>
                                       <td style={{ padding: "15px", textAlign: "right", fontSize: "16px", fontWeight: "600", color: "#3a8e87" }}>
                                          {formatPricePerSquareMeter(analysisResult.estimatedPricePerSquareMeter, props.areaUnit)}
                                       </td>
                                    </tr>
                                 )}
                              </tbody>
                           </table>
                        </div>

                        {analysisResult.estimatedPricePerSquareMeter === null && (
                           <div style={{ marginTop: "20px", color: "#666", fontStyle: "italic" }}>
                              {Localization.getText("Unable to calculate estimated price. Ensure the polygon overlaps with farmland and soil data.")}
                           </div>
                        )}
                     </>
                  )}
               </div>
            )}

            {!selectedLayerId && !isFetchingGeometry && !isCalculating && (
               <div style={{ marginTop: "15px", color: "#666", fontStyle: "italic" }}>
                  {Localization.getText("Select a polygon layer to analyze")}
               </div>
            )}
         </div>
      </div>
   );
}